import React, { useState, useEffect } from 'react';
import { GameManager } from './engine/GameManager';
import type { GameSession, NarrativeEvent, Character } from './types/game';
import { googleGeminiImage, llmChatCompletions } from 'seacloud-sdk';
import { SaveManager, type SaveSlot } from './utils/SaveManager';
import { SoundManager } from './utils/SoundManager';
import { getRandomTip, getEstimatedTime } from './utils/LoadingTips';
import { applyTheme } from './utils/ThemeManager';
import { SaveLoadMenu, SettingsMenu } from './components/UIComponents';
import './App.css';
import './App_features.css';

type GamePhase = 'start' | 'character-creation' | 'world-generation' | 'playing' | 'ended' | 'save-load';

// World theme options
const WORLD_THEMES = [
  { value: 'dark-fantasy', label: 'Dark Fantasy', description: 'Gothic castles, ancient curses, dark magic' },
  { value: 'horror', label: 'Horror', description: 'Psychological terror, supernatural entities, survival' },
  { value: 'isekai', label: 'Isekai', description: 'Transported to another world, game-like mechanics' },
  { value: 'steampunk', label: 'Steampunk', description: 'Victorian era, steam-powered technology, airships' },
  { value: 'cyberpunk', label: 'Cyberpunk', description: 'High-tech dystopia, megacorporations, hackers' },
  { value: 'post-apocalypse', label: 'Post-Apocalypse', description: 'Wasteland survival, scavengers, ruins' },
  { value: 'lovecraftian', label: 'Lovecraftian', description: 'Cosmic horror, ancient gods, madness' },
  { value: 'cultivation', label: 'Cultivation', description: 'Eastern fantasy, martial arts, immortal path' },
  { value: 'space-opera', label: 'Space Opera', description: 'Galactic empires, starships, alien civilizations' },
  { value: 'sword-and-sorcery', label: 'Sword & Sorcery', description: 'Heroic adventures, mighty warriors, powerful magic' }
];

function App() {
  const [gameManager] = useState(() => new GameManager());
  const [phase, setPhase] = useState<GamePhase>('start');
  const [session, setSession] = useState<GameSession | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState<string>('');
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [showCausalFeedback, setShowCausalFeedback] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'inventory' | 'abilities'>('inventory');
  const [characterPortraitUrl, setCharacterPortraitUrl] = useState<string | null>(null);
  const [sceneBackgroundUrl, setSceneBackgroundUrl] = useState<string | null>(null);
  const [currentLocation, setCurrentLocation] = useState<string>('');
  const [naturalInput, setNaturalInput] = useState<string>('');

  // Character creation state
  const [characterName, setCharacterName] = useState<string>('');
  const [characterTraits, setCharacterTraits] = useState<string>('');
  const [selectedTheme, setSelectedTheme] = useState<string>('');
  const [customWorldDescription, setCustomWorldDescription] = useState<string>('');

  // Save/Load state
  const [showSaveMenu, setShowSaveMenu] = useState(false);
  const [saveSlots, setSaveSlots] = useState<(SaveSlot | null)[]>([]);

  // Sound/Music state
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [musicEnabled, setMusicEnabled] = useState(true);

  // Loading tips state
  const [currentTip, setCurrentTip] = useState<string>('');
  const [estimatedTime, setEstimatedTime] = useState<number>(0);

  // Typewriter effect state
  const [displayedText, setDisplayedText] = useState<string>('');
  const [isTyping, setIsTyping] = useState(false);

  // Initialize sound manager
  useEffect(() => {
    SoundManager.init();
    setSoundEnabled(SoundManager.isSoundEnabled());
    setMusicEnabled(SoundManager.isMusicEnabled());
  }, []);

  // Apply theme when world is loaded
  useEffect(() => {
    if (session?.world.type) {
      applyTheme(session.world.type);
    }
  }, [session?.world.type]);

  // Typewriter effect for narrative text
  useEffect(() => {
    if (!session?.currentEvent?.description || phase !== 'playing') {
      return;
    }

    const text = session.currentEvent.description;
    setDisplayedText('');
    setIsTyping(true);

    let index = 0;
    const interval = setInterval(() => {
      if (index < text.length) {
        setDisplayedText(text.substring(0, index + 1));
        index++;
      } else {
        setIsTyping(false);
        clearInterval(interval);
      }
    }, 30); // 30ms per character

    return () => clearInterval(interval);
  }, [session?.currentEvent?.description, phase]);

  // Skip typewriter effect on click
  const skipTypewriter = () => {
    if (isTyping && session?.currentEvent?.description) {
      setDisplayedText(session.currentEvent.description);
      setIsTyping(false);
    }
  };

  // Navigate to character creation
  const handleGoToCharacterCreation = () => {
    SoundManager.playClickSound();
    setPhase('character-creation');
  };

  // Navigate to save/load menu
  const handleOpenSaveLoad = (isSaving: boolean) => {
    SoundManager.playClickSound();
    setSaveSlots(SaveManager.getAllSaves());
    setShowSaveMenu(true);
  };

  // Save game to slot
  const handleSaveToSlot = (slotId: number) => {
    if (!session) return;

    const success = SaveManager.saveGame(slotId, session, characterTraits, selectedTheme);
    if (success) {
      SoundManager.playSuccessSound();
      setSaveSlots(SaveManager.getAllSaves());
      setTimeout(() => setShowSaveMenu(false), 500);
    } else {
      SoundManager.playErrorSound();
    }
  };

  // Load game from slot
  const handleLoadFromSlot = (slotId: number) => {
    const saveData = SaveManager.loadGame(slotId);
    if (!saveData) {
      SoundManager.playErrorSound();
      return;
    }

    SoundManager.playSuccessSound();
    setSession(saveData.session);
    setCharacterTraits(saveData.characterTraits || '');
    setSelectedTheme(saveData.selectedTheme || '');
    setShowSaveMenu(false);
    setPhase('playing');
  };

  // Delete save slot
  const handleDeleteSave = (slotId: number) => {
    if (confirm('Delete this save? This cannot be undone.')) {
      SaveManager.deleteSave(slotId);
      SoundManager.playClickSound();
      setSaveSlots(SaveManager.getAllSaves());
    }
  };

  // Toggle sound
  const handleToggleSound = () => {
    const newState = SoundManager.toggleSound();
    setSoundEnabled(newState);
    SoundManager.playClickSound();
  };

  // Toggle music
  const handleToggleMusic = () => {
    const newState = SoundManager.toggleMusic();
    setMusicEnabled(newState);
    if (newState && session?.world.type) {
      SoundManager.playBackgroundMusic(session.world.type);
    }
  };

  // Use random generation
  const handleRandomGeneration = () => {
    setCharacterName('');
    setCharacterTraits('');
    setSelectedTheme('');
    setCustomWorldDescription('');
    handleStartGameWithParams();
  };

  // Start game with player customization
  const handleStartGameWithParams = async () => {
    setLoading(true);
    setError(null);
    setPhase('world-generation');
    setLoadingProgress(0);
    setCurrentTip(getRandomTip());

    try {
      // Step 1: Generate world with player preferences
      setLoadingStep('Generating world settings...');
      setEstimatedTime(getEstimatedTime('world'));
      setLoadingProgress(20);
      setCurrentTip(getRandomTip());

      // Pass player preferences to game manager
      const playerPreferences = {
        characterName: characterName || undefined,
        characterTraits: characterTraits || undefined,
        worldTheme: selectedTheme || undefined,
        worldDescription: customWorldDescription || undefined
      };

      const world = await gameManager.startNewGame(playerPreferences);

      // Play background music
      if (musicEnabled && world.world.type) {
        SoundManager.playBackgroundMusic(world.world.type);
      }

      setLoadingStep('Generating scene background...');
      setEstimatedTime(getEstimatedTime('background'));
      setLoadingProgress(60);
      setCurrentTip(getRandomTip());

      // Generate initial background (non-blocking)
      const initialLocation = world.currentEvent.location || 'Unknown Land';
      setCurrentLocation(initialLocation);

      // Async background generation, doesn't block game
      generateSceneBackground(initialLocation, world.currentEvent.description)
        .then(bgUrl => {
          if (bgUrl) {
            setSceneBackgroundUrl(bgUrl);
            console.log('[App] ✓ Scene background generated successfully');
          }
        })
        .catch(err => {
          console.warn('[App] ⚠️ Scene background generation failed (does not affect game):', err.message);
        });

      setLoadingStep('Generating character portrait...');
      setEstimatedTime(getEstimatedTime('portrait'));
      setLoadingProgress(80);
      setCurrentTip(getRandomTip());

      // Generate initial character portrait (non-blocking)
      if (world.characters && world.characters.length > 0) {
        const mainCharacter = world.characters.find(c => c.alive) || world.characters[0];

        generateCharacterPortrait(mainCharacter, characterTraits || undefined)
          .then(portraitUrl => {
            if (portraitUrl) {
              setCharacterPortraitUrl(portraitUrl);
              console.log('[App] ✓ Character portrait generated successfully');
            }
          })
          .catch(err => {
            console.warn('[App] ⚠️ Character portrait generation failed (does not affect game):', err.message);
          });
      }

      // Complete initialization (GameManager handles all steps internally)
      setLoadingStep('Initialization complete!');
      setLoadingProgress(100);

      setSession(world);
      setTimeout(() => {
        setPhase('playing');
        setLoadingStep('');
        setLoadingProgress(0);
      }, 300);
    } catch (err: any) {
      console.error('Game initialization error:', err);
      setError(err.message || 'Game initialization failed, please try again');
      setPhase('start');
      setLoadingStep('');
      setLoadingProgress(0);
    } finally {
      setLoading(false);
    }
  };

  // Generate character portrait
  const generateCharacterPortrait = async (character: Character, customTraits?: string): Promise<string | null> => {
    try {
      let prompt = '';
      if (customTraits) {
        // Use player-provided character traits
        prompt = `A character portrait of ${character.name}, ${customTraits}. Standing pose, full body, anime game art style, detailed pixel art aesthetic, clean background, professional character design, consistent art style.`;
      } else {
        // Use default character description
        prompt = `A character portrait of ${character.name}, ${character.role}. Standing pose, full body, anime game art style, detailed pixel art aesthetic, clean background, professional character design, consistent art style.`;
      }
      console.log('[App] Generating character portrait:', prompt);

      const result = await googleGeminiImage({
        prompt,
        aspect_ratio: '4:3'
      });

      if (result && result.length > 0 && result[0].url) {
        return result[0].url;
      }
      return null;
    } catch (err) {
      console.error('[App] Character portrait generation failed:', err);
      return null;
    }
  };

  // Generate scene background
  const generateSceneBackground = async (location: string, description: string): Promise<string | null> => {
    try {
      const prompt = `Background scene: ${location}. ${description}. Anime game background art style, atmospheric lighting, detailed environment, high quality digital painting, no characters, cinematic composition.`;
      console.log('[App] Generating scene background:', prompt);

      const result = await googleGeminiImage({
        prompt,
        aspect_ratio: '16:9'
      });

      if (result && result.length > 0 && result[0].url) {
        return result[0].url;
      }
      return null;
    } catch (err) {
      console.error('[App] Scene background generation failed:', err);
      return null;
    }
  };

  // Execute choice
  const handleChoice = async (choiceId: string) => {
    if (!session) return;

    // Play sound based on risk level
    const choice = session.currentEvent.choices.find(c => c.id === choiceId);
    if (choice) {
      SoundManager.playChoiceSound(choice.risk);
    }

    console.log('[App] User choice:', choiceId);
    setLoading(true);
    setError(null);
    setCurrentTip(getRandomTip());
    setEstimatedTime(getEstimatedTime('event'));

    try {
      console.log('[App] Calling gameManager.makeChoice...');
      const result = await gameManager.makeChoice(choiceId);
      console.log('[App] makeChoice success:', result);

      // Show causal feedback (short version)
      const shortFeedback = result.causalEvent.consequences?.[0] || 'Your choice has an impact...';
      setShowCausalFeedback(shortFeedback);
      setTimeout(() => setShowCausalFeedback(null), 3000);

      // Check if location changed, async generate new background (non-blocking)
      const newLocation = result.session.currentEvent.location || '';
      if (newLocation && newLocation !== currentLocation) {
        console.log('[App] Location changed, generating new background:', newLocation);
        setCurrentLocation(newLocation);
        generateSceneBackground(
          newLocation,
          result.session.currentEvent.description
        )
          .then(bgUrl => {
            if (bgUrl) {
              setSceneBackgroundUrl(bgUrl);
              console.log('[App] ✓ Scene background generated successfully');
            }
          })
          .catch(err => {
            console.warn('[App] ⚠️ Scene background generation failed (does not affect game):', err.message);
          });
      }

      // Async generate new character portrait (non-blocking)
      if (result.session.characters && result.session.characters.length > 0) {
        const mainCharacter = result.session.characters.find(c => c.alive) || result.session.characters[0];
        console.log('[App] Generating new character portrait:', mainCharacter.name);
        generateCharacterPortrait(mainCharacter)
          .then(portraitUrl => {
            if (portraitUrl) {
              setCharacterPortraitUrl(portraitUrl);
              console.log('[App] ✓ Character portrait generated successfully');
            }
          })
          .catch(err => {
            console.warn('[App] ⚠️ Character portrait generation failed (does not affect game):', err.message);
          });
      }

      setSession(result.session);

      if (result.session.isEnded) {
        setPhase('ended');
      }
    } catch (err: any) {
      console.error('[App] makeChoice error:', err);
      setError(err.message || 'Failed to process choice');
    } finally {
      setLoading(false);
    }
  };

  // Restart game
  const handleRestart = () => {
    setPhase('start');
    setSession(null);
    setError(null);
    setShowCausalFeedback(null);
    setCharacterPortraitUrl(null);
    setSceneBackgroundUrl(null);
    setCurrentLocation('');
    setNaturalInput('');
  };

  // Handle natural language input
  const handleNaturalInput = async () => {
    if (!naturalInput.trim() || !session) return;

    console.log('[App] Natural language input:', naturalInput);
    setLoading(true);
    setError(null);

    try {
      // Use AI to convert natural language to game action
      const prompt = `Current game scene: ${session.currentEvent.description}

Available actions:
${session.currentEvent.choices.map((c, i) => `${i + 1}. ${c.text}`).join('\n')}

Player input: "${naturalInput}"

Please analyze the player's intent, choose the most matching action from the available actions, or if the player wants to do something not in the list, creatively interpret how this action will affect the story.

Please respond in JSON format:
{
  "matchedChoiceIndex": <number or null>,
  "customAction": "<if no matching option, describe the result of custom action>",
  "response": "<response to the player>"
}`;

      const aiResponse = await llmChatCompletions({
        model: 'seaart-mix-sonnet-4-5',
        messages: [{ role: 'user', content: prompt }],
        temperature: 0.7,
        max_tokens: 500,
        timeout: 60000
      });

      const content = aiResponse.choices[0]?.message?.content || '';
      console.log('[App] AI response:', content);

      // Parse AI response
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const result = JSON.parse(jsonMatch[0]);

        // Show AI response
        setShowCausalFeedback(result.response);
        setTimeout(() => setShowCausalFeedback(null), 4000);

        // If matched an option, auto execute
        if (result.matchedChoiceIndex !== null && result.matchedChoiceIndex >= 0) {
          const choiceIndex = result.matchedChoiceIndex;
          if (session.currentEvent.choices[choiceIndex]) {
            setTimeout(() => {
              handleChoice(session.currentEvent.choices[choiceIndex].id);
            }, 1000);
          }
        }
      } else {
        setShowCausalFeedback('AI is thinking about your action...');
        setTimeout(() => setShowCausalFeedback(null), 3000);
      }

      setNaturalInput('');
    } catch (err: any) {
      console.error('[App] Processing natural input error:', err);
      setError(err.message || 'Failed to process input');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app">
      {/* Background effects */}
      <div className="bg-overlay"></div>
      <div className="bg-vignette"></div>

      {/* Main container */}
      <div className="container">
        {/* Title - only shown during start and initialization phases */}
        {(phase === 'start' || phase === 'character-creation' || phase === 'initializing') && (
          <header className="header">
            <h1 className="title">Instantale</h1>
            <p className="subtitle">AI GENERATED LEGENDS</p>
          </header>
        )}

        {/* Start screen */}
        {phase === 'start' && (
          <div className="start-screen">
            <div className="start-content">
              <p className="game-desc">
                AI-powered Roguelike narrative adventure<br />
                Every playthrough is a unique story<br />
                Your choices weave the web of fate
              </p>
              <div className="start-buttons">
                <button
                  className="btn-primary"
                  onClick={handleGoToCharacterCreation}
                  disabled={loading}
                >
                  Create Character
                </button>
                <button
                  className="btn-secondary"
                  onClick={handleRandomGeneration}
                  disabled={loading}
                >
                  {loading ? 'Generating...' : 'Quick Start (Random)'}
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => {
                    setSaveSlots(SaveManager.getAllSaves());
                    setShowSaveMenu(true);
                  }}
                  disabled={loading}
                >
                  📂 Load Game
                </button>
              </div>

              {/* Progress bar */}
              {loading && (
                <div className="loading-container">
                  <div className="loading-text">{loadingStep}</div>
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${loadingProgress}%` }}
                    ></div>
                  </div>
                  <div className="loading-hint">
                    Generating, estimated 20-40 seconds, please wait...
                  </div>
                </div>
              )}

              {error && (
                <div className="error-msg">
                  {error}
                  <br />
                  <small>Tip: If it keeps failing, please check network connection or try again later</small>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Character creation screen */}
        {phase === 'character-creation' && (
          <div className="character-creation-screen">
            <header className="header">
              <h1 className="title">Create Your Legend</h1>
              <p className="subtitle">Customize your character and world</p>
            </header>

            <div className="creation-content">
              <div className="creation-form">
                {/* Character name input */}
                <div className="form-section">
                  <label className="form-label">
                    <span className="label-icon">👤</span>
                    Character Name
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter your character's name (optional)"
                    value={characterName}
                    onChange={(e) => setCharacterName(e.target.value)}
                    maxLength={30}
                  />
                  <p className="form-hint">Leave blank for AI-generated name</p>
                </div>

                {/* Character traits input */}
                <div className="form-section">
                  <label className="form-label">
                    <span className="label-icon">✨</span>
                    Character Traits
                  </label>
                  <textarea
                    className="form-textarea"
                    placeholder="Describe your character's appearance and personality (e.g., 'brave warrior with silver hair and blue eyes, skilled in swordsmanship')"
                    value={characterTraits}
                    onChange={(e) => setCharacterTraits(e.target.value)}
                    maxLength={200}
                    rows={3}
                  />
                  <p className="form-hint">Used for generating your character portrait</p>
                </div>

                {/* World theme selection */}
                <div className="form-section">
                  <label className="form-label">
                    <span className="label-icon">🌍</span>
                    World Theme
                  </label>
                  <div className="theme-grid">
                    {WORLD_THEMES.map(theme => (
                      <button
                        key={theme.value}
                        className={`theme-card ${selectedTheme === theme.value ? 'selected' : ''}`}
                        onClick={() => setSelectedTheme(theme.value)}
                      >
                        <div className="theme-label">{theme.label}</div>
                        <div className="theme-description">{theme.description}</div>
                      </button>
                    ))}
                  </div>
                  <p className="form-hint">Select a world theme or leave blank for random</p>
                </div>

                {/* Custom world description (optional) */}
                <div className="form-section">
                  <label className="form-label">
                    <span className="label-icon">📜</span>
                    Custom World Description (Optional)
                  </label>
                  <textarea
                    className="form-textarea"
                    placeholder="Describe your own unique world setting (this will override the selected theme)"
                    value={customWorldDescription}
                    onChange={(e) => setCustomWorldDescription(e.target.value)}
                    maxLength={300}
                    rows={3}
                  />
                  <p className="form-hint">Advanced: Create your own custom world</p>
                </div>

                {/* Action buttons */}
                <div className="creation-actions">
                  <button
                    className="btn-secondary"
                    onClick={() => setPhase('start')}
                  >
                    ← Back
                  </button>
                  <button
                    className="btn-primary"
                    onClick={handleStartGameWithParams}
                    disabled={loading}
                  >
                    {loading ? 'Generating...' : 'Start Adventure →'}
                  </button>
                </div>

                {/* Progress bar */}
                {loading && (
                  <div className="loading-container">
                    <div className="loading-text">{loadingStep}</div>
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${loadingProgress}%` }}
                      ></div>
                    </div>
                    <div className="loading-hint">
                      Generating your personalized adventure...
                    </div>
                  </div>
                )}

                {error && (
                  <div className="error-msg">
                    {error}
                    <br />
                    <small>Tip: If it keeps failing, please check network connection or try again later</small>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* World generation */}
        {phase === 'world-generation' && (
          <div className="loading-screen">
            <div className="spinner"></div>
            <p className="loading-text">{loadingStep}</p>
            <p className="loading-subtext">{currentTip}</p>
            {estimatedTime > 0 && (
              <p className="loading-estimate">~{estimatedTime} seconds remaining</p>
            )}
            <div className="progress-bar-full">
              <div
                className="progress-fill"
                style={{ width: `${loadingProgress}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* Game in progress - immersive dialogue layout */}
        {phase === 'playing' && session && session.currentEvent && (
          <div className="immersive-layout">
            {/* Top control bar */}
            <div className="game-controls">
              <button className="control-btn" onClick={() => handleOpenSaveLoad(true)} title="Save Game">
                💾 Save
              </button>
              <button className="control-btn" onClick={() => handleOpenSaveLoad(false)} title="Load Game">
                📂 Load
              </button>
              <SettingsMenu
                soundEnabled={soundEnabled}
                musicEnabled={musicEnabled}
                onToggleSound={handleToggleSound}
                onToggleMusic={handleToggleMusic}
              />
            </div>

            {/* Left - dialogue/action options panel (Action Panel) */}
            <div className="action-panel">
              <div className="action-list">
                {!loading && session.currentEvent.choices.map(choice => (
                  <button
                    key={choice.id}
                    className="action-option"
                    onClick={() => handleChoice(choice.id)}
                  >
                    <span className="option-text">{choice.text}</span>
                    {choice.risk && (
                      <span className={`risk-badge risk-${choice.risk}`}>
                        {choice.risk === 'high' ? 'High Risk' : choice.risk === 'medium' ? 'Medium Risk' : 'Low Risk'}
                      </span>
                    )}
                  </button>
                ))}
                {loading && (
                  <div className="action-loading">
                    <div className="loading-dot"></div>
                    <span>AI thinking...</span>
                  </div>
                )}
              </div>
            </div>

            {/* Center - scene display area (Stage) */}
            <div className="stage-area" style={{
              backgroundImage: sceneBackgroundUrl ? `url(${sceneBackgroundUrl})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}>
              <div className="scene-backdrop">
                <div className="scene-ambient"></div>
                <div className="scene-info">
                  <span className="location-tag">📍 {session.currentEvent.location || 'Unknown Land'}</span>
                </div>
              </div>
            </div>

            {/* Right - character info panel */}
            <div className="character-info-panel">
              {/* Top: Character name and role */}
              <div className="character-header">
                <h3 className="character-name">
                  {session.characters.find(c => c.alive)?.name || 'Unknown'}
                </h3>
                <div className="character-traits">
                  <span className="trait-tag">{session.characters[0]?.role || 'Unknown'}</span>
                </div>
              </div>

              {/* Middle: Tabs */}
              <div className="info-tabs">
                <button
                  className={`tab-button ${activeTab === 'inventory' ? 'active' : ''}`}
                  onClick={() => setActiveTab('inventory')}
                >
                  Inventory
                </button>
                <button
                  className={`tab-button ${activeTab === 'abilities' ? 'active' : ''}`}
                  onClick={() => setActiveTab('abilities')}
                >
                  Abilities
                </button>
              </div>

              <div className="tab-content">
                {activeTab === 'inventory' && (
                  <div className="inventory-list">
                    {session.playerIdentity?.inventory && session.playerIdentity.inventory.length > 0 ? (
                      session.playerIdentity.inventory.map((item, idx) => (
                        <div key={idx} className="inventory-item">
                          <span className="item-icon">📦</span>
                          <span className="item-name">{item}</span>
                        </div>
                      ))
                    ) : (
                      <div className="empty-state">No items</div>
                    )}
                  </div>
                )}
                {activeTab === 'abilities' && (
                  <div className="abilities-list">
                    {session.playerIdentity?.skills && session.playerIdentity.skills.length > 0 ? (
                      session.playerIdentity.skills.map((skill, idx) => (
                        <div key={idx} className="ability-item">
                          <span className="ability-icon">⚡</span>
                          <span className="ability-name">{skill}</span>
                        </div>
                      ))
                    ) : (
                      <div className="empty-state">No abilities</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Bottom left - character portrait area */}
            <div className="character-portrait-area">
              <div className="character-stage">
                <div className="character-portrait-main">
                  <div className="portrait-frame">
                    <div className="portrait-image" style={{
                      backgroundImage: characterPortraitUrl ? `url(${characterPortraitUrl})` : 'none',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}>
                      {!characterPortraitUrl && (
                        <div className="portrait-placeholder-text">Character Portrait</div>
                      )}
                    </div>
                    <div className="character-emotion">
                      <span className="emotion-tag">😐 Calm</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom center - narrative text box */}
            <div className="narrative-log">
              <div className="log-content" onClick={skipTypewriter}>
                <div className="narrative-text">
                  {displayedText}
                  {isTyping && <span className="typing-cursor">▌</span>}
                </div>
                {isTyping && (
                  <div className="skip-hint">Click to skip...</div>
                )}
              </div>

              {/* Free input area (AI core) */}
              <div className="input-zone">
                <input
                  type="text"
                  className="natural-input"
                  placeholder="Type anything you want to do..."
                  value={naturalInput}
                  onChange={(e) => setNaturalInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !loading) {
                      handleNaturalInput();
                    }
                  }}
                  disabled={loading}
                />
                <button
                  className="send-button"
                  onClick={handleNaturalInput}
                  disabled={loading || !naturalInput.trim()}
                >
                  <span>→</span>
                </button>
              </div>
            </div>

            {/* Bottom right - character relationship area */}
            <div className="character-relationship-area">
              <div className="relationship-header">
                <h4 className="relationship-title">Scene Characters</h4>
              </div>

              {/* Display all characters in current scene */}
              {session.characters && session.characters.filter(c => c.alive).length > 0 ? (
                <div className="characters-list">
                  {session.characters.filter(c => c.alive).map((character, idx) => (
                    <div key={idx} className="character-card">
                      <div className="character-card-header">
                        <span className="character-card-name">{character.name}</span>
                        <span className="character-card-role">{character.role}</span>
                      </div>
                      <div className="relationship-status">
                        <span className="status-label">Relationship</span>
                        <div className="relationship-bar">
                          <div
                            className="relationship-fill"
                            style={{
                              width: `${Math.abs(character.relationship || 0)}%`,
                              background: (character.relationship || 0) > 0
                                ? 'linear-gradient(90deg, #4ade80, #22c55e)'
                                : 'linear-gradient(90deg, #ef4444, #dc2626)'
                            }}
                          ></div>
                        </div>
                        <span className="status-value">{character.relationship || 0}</span>
                      </div>
                      {character.secrets && character.secrets.length > 0 && (
                        <div className="character-secrets">
                          <span className="secrets-label">🔒 Secrets: {character.secrets.length}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">No characters in current scene</div>
              )}

              <div className="meta-info">
                <div className="meta-item">
                  <span className="meta-label">Class</span>
                  <span className="meta-value">{session.playerIdentity?.socialClass || 'Commoner'}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Turn</span>
                  <span className="meta-value">{session.worldState?.turnCount || 0}</span>
                </div>
              </div>
            </div>

            {/* Causal feedback bar */}
            {showCausalFeedback && (
              <div className="causal-feedback-bar">
                <div className="causal-icon">⚡</div>
                <p>{showCausalFeedback}</p>
              </div>
            )}

            {error && (
              <div className="error-overlay">
                <div style={{fontWeight: 'bold', marginBottom: '10px'}}>❌ Error</div>
                <div>{error}</div>
                <div style={{marginTop: '10px', fontSize: '12px', opacity: 0.8}}>
                  Tip: Check browser console for detailed error information
                </div>
              </div>
            )}
          </div>
        )}

        {/* Ending screen */}
        {phase === 'ended' && session && (
          <div className="ending-screen">
            <div className="ending-content">
              <h2 className="ending-title">
                {session.endingType === 'victory' && '✨ Victory Ending'}
                {session.endingType === 'defeat' && '💀 Tragic Ending'}
                {session.endingType === 'neutral' && '⚖️ Balanced Ending'}
                {session.endingType === 'transcendent' && '🌟 Transcendent Ending'}
              </h2>

              <div className="ending-narrative">
                {session.currentEvent?.description}
              </div>

              <div className="ending-stats">
                <h3>Journey Summary</h3>
                <div className="stats-grid">
                  <div className="stat-item">
                    <span className="stat-label">Turns</span>
                    <span className="stat-value">{session.worldState.turnCount}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Alive Characters</span>
                    <span className="stat-value">
                      {session.characters.filter(c => c.alive).length} / {session.characters.length}
                    </span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">World Tension</span>
                    <span className="stat-value">{session.worldState.tension}</span>
                  </div>
                </div>
              </div>

              <button className="btn-primary" onClick={handleRestart}>
                Weave Fate Again
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Save/Load Menu Overlay */}
      {showSaveMenu && (
        <SaveLoadMenu
          saveSlots={saveSlots}
          isSaving={phase === 'playing'}
          onSave={handleSaveToSlot}
          onLoad={handleLoadFromSlot}
          onDelete={handleDeleteSave}
          onClose={() => setShowSaveMenu(false)}
        />
      )}
    </div>
  );
}

// Status bar component
function StatusBar({ session }: { session: GameSession }) {
  const stats = {
    turns: session.worldState.turnCount,
    tension: session.worldState.tension,
    alive: session.characters.filter(c => c.alive).length
  };

  return (
    <div className="status-bar">
      <div className="status-item">
        <span className="status-label">Turn</span>
        <span className="status-value">{stats.turns}</span>
      </div>
      <div className="status-item">
        <span className="status-label">Tension</span>
        <div className="tension-bar">
          <div
            className="tension-fill"
            style={{ width: `${stats.tension}%` }}
          ></div>
        </div>
        <span className="status-value">{stats.tension}</span>
      </div>
      <div className="status-item">
        <span className="status-label">Alive</span>
        <span className="status-value">{stats.alive}</span>
      </div>
    </div>
  );
}

// Narrative display component
function NarrativeDisplay({ event }: { event: NarrativeEvent }) {
  return (
    <div className="narrative-card">
      <div className="narrative-header">
        <h2 className="event-title">{event.title}</h2>
        {event.location && (
          <span className="event-location">📍 {event.location}</span>
        )}
      </div>
      <div className="narrative-body">
        <p className="event-description">{event.description}</p>
      </div>
    </div>
  );
}

// Character panel component
function CharacterPanel({ characters }: { characters: Character[] }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`character-panel ${expanded ? 'expanded' : ''}`}>
      <button
        className="panel-toggle"
        onClick={() => setExpanded(!expanded)}
      >
        <span>Characters</span>
        <span className="toggle-icon">{expanded ? '▼' : '▲'}</span>
      </button>

      {expanded && (
        <div className="character-list">
          {characters.map(char => (
            <div
              key={char.id}
              className={`character-card ${!char.alive ? 'dead' : ''}`}
            >
              <div className="char-header">
                <h4 className="char-name">
                  {char.name}
                  {!char.alive && ' 💀'}
                </h4>
                <span className="char-role">{char.role}</span>
              </div>
              <p className="char-desc">{char.description}</p>
              <div className="char-relationship">
                <span className="rel-label">Relationship:</span>
                <div className="rel-bar">
                  <div
                    className="rel-fill"
                    style={{
                      width: `${Math.abs(char.relationship)}%`,
                      background: char.relationship > 0
                        ? 'linear-gradient(90deg, #4ade80, #22c55e)'
                        : 'linear-gradient(90deg, #ef4444, #dc2626)'
                    }}
                  ></div>
                </div>
                <span className="rel-value">{char.relationship}</span>
              </div>
              {char.fate && (
                <p className="char-fate">
                  <em>"{char.fate}"</em>
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default App;
